import { useTranslation } from 'react-i18next';
/**
 * ContractsTab.js
 *
 * Top-level Contract Tracking module. Three sub-tabs:
 *   1. Contracts — the live list with status badges and PDF download
 *      (ContractsList + ContractCreateDialog stay here as they're the
 *      module's entry point)
 *   2. Templates — manage paragraph-based templates with token picker
 *      (TemplatesList stays here; the heavy TemplateEditor lives in
 *      ./contracts/TemplateEditor.js)
 *   3. Stamps & Signatories — see ./contracts/SignatoriesList.js
 *
 * The big sub-components have been extracted into ./contracts/* for
 * readability and faster dev-server reloads.
 */
import { WeekFilter } from './ui/week-filter';
import { weekToRange } from '../lib/weekFilter';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  FileText, Plus, Download, Pencil, Trash2, Copy, Languages,
  FileSignature, X,
} from 'lucide-react';
import { API, STATUS_BADGE, KIND_LABEL, filenameFromContentDisposition } from './contracts/_shared';
import ContractEditDialog from './contracts/ContractEditDialog';
import TemplateEditor from './contracts/TemplateEditor';
import SignatoriesList from './contracts/SignatoriesList';
import PaginationFooter from './PaginationFooter';
import useDeepShortcut from '../hooks/useDeepShortcut';
import useSubTabShortcut from '../hooks/useSubTabShortcut';

export default function ContractsTab({ navContext }) {
  const { t } = useTranslation();
  const [subtab, setSubtab] = useState('contracts');
  useSubTabShortcut({
    tab: 'contracts', ctxKey: 'contractsSubTab', value: subtab, setValue: setSubtab,
    labels: { contracts: 'auto.ctr.documents', templates: 'auto.ctr.templates', signatories: 'contracts.tabs.signatories' },
  });
  return (
    <div className="space-y-4" data-testid="contracts-tab">
      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="contracts" data-testid="contracts-subtab-contracts">
            <FileSignature className="h-4 w-4 mr-1" /> {t('auto.ctr.documents')}
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="contracts-subtab-templates">
            <FileText className="h-4 w-4 mr-1" /> {t('auto.ctr.templates')}
          </TabsTrigger>
          <TabsTrigger value="signatories" data-testid="contracts-subtab-signatories">
            <FileSignature className="h-4 w-4 mr-1" /> {t('contracts.tabs.signatories')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="contracts"><ContractsList navContext={navContext} /></TabsContent>
        <TabsContent value="templates"><TemplatesList /></TabsContent>
        <TabsContent value="signatories"><SignatoriesList /></TabsContent>
      </Tabs>
    </div>
  );
}


// ===== Contracts list ===============================================
function ContractsList({ navContext }) {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState({ status: '', kind: '', q: '' });
  const [weekFilter, setWeekFilter] = useState(null);
  // Server-side pagination state — drives the skip/limit query params.
  const [pager, setPager] = useState({ skip: 0, limit: 25 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Dashboard shortcuts: pin the contract the user has open.
  useDeepShortcut({
    active: !!editingId,
    tab: 'contracts',
    context: { focusContractId: editingId },
    label: contracts.find((c) => c.id === editingId)?.contract_number
      || contracts.find((c) => c.id === editingId)?.title || 'Contract',
    subtitle: 'Contracts',
    icon: 'contracts',
    perms: ['contracts.view'],
    priority: 3,
    focusKey: 'focusContractId',
    onFocus: (id) => setEditingId(id),
  });

  // Fetch contracts whenever the kind/status filter or pager changes.
  // Refs (templates/employees/customers/projects) only need to load
  // once; we fetch them in a separate one-shot effect.
  const fetchContracts = async () => {
    const params = new URLSearchParams();
    params.set('skip', pager.skip);
    params.set('limit', pager.limit);
    if (filter.kind) params.set('kind', filter.kind);
    if (filter.status) params.set('status', filter.status);
    if (weekFilter) {
      const r0 = weekToRange(weekFilter);
      params.set('date_from', r0.from);
      params.set('date_to', r0.to);
    }
    const r = await axios.get(`${API}/contracts?${params.toString()}`);
    setContracts(r.data || []);
    setTotal(parseInt(r.headers['x-total-count'] || '0', 10));
  };

  useEffect(() => {
    fetchContracts();
  }, [pager.skip, pager.limit, filter.kind, filter.status, weekFilter]);

  useEffect(() => {
    // One-shot fetch of reference data used by the create + edit dialogs.
    Promise.all([
      axios.get(`${API}/contract-templates?active_only=true`),
      axios.get(`${API}/employees`),
      axios.get(`${API}/customers`),
      axios.get(`${API}/projects`),
    ]).then(([t, e, cu, p]) => {
      setTemplates(t.data || []);
      setEmployees(e.data || []);
      setCustomers(cu.data || []);
      setProjects(p.data || []);
    });
  }, []);

  // Text-search `q` filters the CURRENT page only — server-side
  // pagination means a `q` over the full archive would need a
  // dedicated search index. We surface a small hint when this matters.
  const filtered = useMemo(() => {
    if (!filter.q) return contracts;
    const q = filter.q.toLowerCase();
    return contracts.filter((c) => {
      const blob = `${c.contract_number} ${c.title || ''} ${c.party_a?.name || ''} ${c.party_b?.name || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [contracts, filter.q]);

  const searchActiveAcrossPages = filter.q && total > pager.limit;

  // Deep link from the global search palette.
  useEffect(() => {
    if (navContext?.searchContract) {
      setFilter((prev) => ({ ...prev, q: navContext.searchContract }));
      setPager((prev) => ({ ...prev, skip: 0 }));
    }
  }, [navContext]);

  // When the user changes kind/status filters, jump back to page 1.
  const setKindFilter = (kind) => { setPager(prev => ({ ...prev, skip: 0  })); setFilter(prev => ({ ...prev, kind  })); };
  const setStatusFilter = (status) => { setPager(prev => ({ ...prev, skip: 0  })); setFilter(prev => ({ ...prev, status  })); };

  const reload = () => fetchContracts();

  const downloadPdf = async (cid, number) => {
    try {
      const r = await axios.get(`${API}/contracts/${cid}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      // Backend Content-Disposition is the single source of truth for
      // filenames (Type-Number-PartyA-PartyB-dd_mm_yyyy.pdf).
      a.download = filenameFromContentDisposition(
        r.headers['content-disposition'],
        `Document-${(number || cid).replace(/\//g, '-')}.pdf`,
      );
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('auto.ctr.pdfGenerationFailed')); }
  };

  const handleDelete = async (cid) => {
    if (!window.confirm(t('auto.ctr.deleteThisDocumentSignedDocumentsWill'))) return;
    try {
      await axios.delete(`${API}/contracts/${cid}`);
      toast.success(t('auto.ctr.removed'));
      reload();
    } catch (e) { toast.error(t('auto.ctr.deleteFailed')); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> {t('auto.ctr.documents')}
          </CardTitle>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="contracts-new-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('auto.ctr.newDocument')}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Input placeholder={t('auto.ctr.searchCurrentPage')} className="max-w-xs h-8"
            value={filter.q} onChange={(e) => setFilter(prev => ({ ...prev, q: e.target.value  }))}
            data-testid="contracts-search" />
          <WeekFilter value={weekFilter} onChange={(w) => { setPager(prev => ({ ...prev, skip: 0 })); setWeekFilter(w); }} testId="contracts-week-filter" />
          <Select value={filter.kind || 'all'} onValueChange={(v) => setKindFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 h-8"><SelectValue placeholder={t('auto.ctr.kind')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.ctr.allKinds')}</SelectItem>
              {Object.entries(KIND_LABEL).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.status || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 h-8"><SelectValue placeholder={t('auto.ctr.status')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.ctr.allStatuses')}</SelectItem>
              {Object.keys(STATUS_BADGE).map((s) => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filter.q || filter.kind || filter.status) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setPager(prev => ({ ...prev, skip: 0  })); setFilter({ status: '', kind: '', q: '' }); }}
              className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="contracts-clear-filters-btn"
              title={t('auto.ctr.resetSearchKindAndStatusFilters')}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              {t('auto.ctr.clearFilters')}
            </Button>
          )}
        </div>
        {searchActiveAcrossPages && (
          <p className="text-[11px] text-amber-700 mt-1.5" data-testid="contracts-search-hint">
            {t('auto.ctr.searchFiltersTheCurrentPageOnly')}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {contracts.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            {total === 0 ? t('auto.ctr.noDocumentsYet') : t('auto.ctr.noDocumentsOnThisPage')}
          </div>
        ) : (
          <>
            {filter.q && filtered.length === 0 && (
              <div className="text-sm text-amber-600 italic py-3 text-center" data-testid="contracts-no-match">
                No documents on this page match &quot;{filter.q}&quot;.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-2 py-2">{t('auto.ctr.number')}</th>
                    <th className="text-left px-2 py-2">{t('auto.ctr.title')}</th>
                    <th className="text-left px-2 py-2">{t('auto.ctr.kind')}</th>
                    <th className="text-left px-2 py-2">{t('auto.ctr.partyA')}</th>
                    <th className="text-left px-2 py-2">{t('auto.ctr.partyB')}</th>
                    <th className="text-left px-2 py-2">{t('auto.ctr.status')}</th>
                    <th className="text-right px-2 py-2 w-32">{t('auto.ctr.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                      <td className="px-2 py-2 font-mono text-xs">{c.contract_number}</td>
                      <td className="px-2 py-2 truncate max-w-xs">{c.title || '—'}</td>
                      <td className="px-2 py-2">
                        <Badge variant="outline">{KIND_LABEL[c.kind] || c.kind}</Badge>
                      </td>
                      <td className="px-2 py-2 truncate max-w-[180px]">{c.party_a?.name || '—'}</td>
                      <td className="px-2 py-2 truncate max-w-[180px]">{c.party_b?.name || '—'}</td>
                      <td className="px-2 py-2">
                        <Badge className={`${STATUS_BADGE[c.status] || ''} text-[10px]`}>
                          {(c.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.ctr.edit')}
                          onClick={() => setEditingId(c.id)} data-testid={`contract-edit-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.ctr.downloadPdf')}
                          onClick={() => downloadPdf(c.id, c.contract_number)} data-testid={`contract-pdf-${c.id}`}>
                          <Download className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.ctr.delete')}
                          onClick={() => handleDelete(c.id)} data-testid={`contract-del-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <PaginationFooter
          total={total}
          skip={pager.skip}
          limit={pager.limit}
          onChange={setPager}
          testIdPrefix="contracts-pagination"
        />
      </CardContent>

      {createOpen && (
        <ContractCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); reload(); }}
          templates={templates}
          employees={employees}
          customers={customers}
          projects={projects}
        />
      )}
      {editingId && (
        <ContractEditDialog
          contractId={editingId}
          open={!!editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); reload(); }}
          employees={employees}
          customers={customers}
          projects={projects}
        />
      )}
    </Card>
  );
}


// ===== Create dialog ================================================
function ContractCreateDialog({ open, onClose, onSaved, templates, employees, customers, projects }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    template_id: '', employee_id: '', customer_id: '', project_id: '',
    signatory_id: '', title: '', valid_from: '', valid_to: '',
    place_of_signing: 'Szczecin', sign_date: new Date().toISOString().slice(0, 10),
  });
  const [signatories, setSignatories] = useState([]);
  const tpl = templates.find((t) => t.id === form.template_id);
  const needsEmployee = tpl?.party_b_type === 'employee' || tpl?.party_a_type === 'employee';
  const needsCustomer = tpl?.party_b_type === 'customer' || tpl?.party_a_type === 'customer';
  const allowsSignatory = tpl?.kind === 'support_letter' || tpl?.kind === 'client_letter';

  useEffect(() => {
    if (!allowsSignatory) { setSignatories([]); return; }
    const reqs = [axios.get(`${API}/client-stamp-signatories?scope=internal`)];
    if (form.customer_id) {
      reqs.push(axios.get(`${API}/client-stamp-signatories?customer_id=${form.customer_id}`));
    }
    Promise.all(reqs)
      .then((rs) => {
        const merged = [];
        const seen = new Set();
        for (const r of rs) {
          for (const s of (r.data || [])) {
            if (!seen.has(s.id)) { seen.add(s.id); merged.push(s); }
          }
        }
        setSignatories(merged);
      })
      .catch(() => setSignatories([]));
  }, [allowsSignatory, form.customer_id]);

  const submit = async () => {
    if (!form.template_id) { toast.error(t('auto.ctr.pickATemplate')); return; }
    if (needsEmployee && !form.employee_id) { toast.error(t('auto.ctr.pickEmployee')); return; }
    if (needsCustomer && !form.customer_id) { toast.error(t('auto.ctr.pickCustomer')); return; }
    try {
      const payload = { ...form };
      if (!payload.signatory_id) delete payload.signatory_id;
      await axios.post(`${API}/contracts`, payload);
      toast.success(t('auto.ctr.documentDrafted'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="contract-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('auto.ctr.newDocument')}</DialogTitle>
          <DialogDescription>{t('auto.ctr.pickATemplateTheDocumentType')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('auto.ctr.template')}</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm(prev => ({ ...prev, template_id: v  }))}>
              <SelectTrigger data-testid="contract-create-template"><SelectValue placeholder={t('auto.ctr.chooseTemplate')} /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {KIND_LABEL[t.kind]} ({t.language?.toUpperCase()})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {needsEmployee && (
              <div>
                <Label>{t('auto.ctr.employee')}</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v  }))}>
                  <SelectTrigger data-testid="contract-create-employee"><SelectValue placeholder={t('auto.ctr.pickEmployee2')} /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {needsCustomer && (
              <div>
                <Label>{t('auto.ctr.customer')}</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm(prev => ({ ...prev, customer_id: v  }))}>
                  <SelectTrigger data-testid="contract-create-customer"><SelectValue placeholder={t('auto.ctr.pickCustomer2')} /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t('auto.ctr.projectOptional')}</Label>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, project_id: v === 'none' ? '' : v  }))}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.ctr.noProject')}</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {allowsSignatory && (
              <div>
                <Label>{t('auto.ctr.customerStampSignatory')}</Label>
                <Select value={form.signatory_id || 'none'}
                  onValueChange={(v) => setForm(prev => ({ ...prev, signatory_id: v === 'none' ? '' : v  }))}>
                  <SelectTrigger data-testid="contract-create-signatory">
                    <SelectValue placeholder={form.customer_id ? '—' : 'Pick a customer first'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('auto.ctr.none')}</SelectItem>
                    {signatories.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.role_label ? ` (${s.role_label})` : ''}
                        {!s.customer_id ? ' · Expertfix' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.customer_id && signatories.length === 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    No signatories for this customer yet — add one under Stamps &amp; Signatories.
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>{t('auto.ctr.titleOptional')}</Label>
            <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value  }))}
              placeholder={t('auto.ctr.defaultsToTemplateName')} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.ctr.signDate')}</Label>
              <Input type="date" value={form.sign_date} onChange={(e) => setForm(prev => ({ ...prev, sign_date: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.placeOfSigning')}</Label>
              <Input value={form.place_of_signing} onChange={(e) => setForm(prev => ({ ...prev, place_of_signing: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.validFrom')}</Label>
              <Input type="date" value={form.valid_from} onChange={(e) => setForm(prev => ({ ...prev, valid_from: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.validTo')}</Label>
              <Input type="date" value={form.valid_to} onChange={(e) => setForm(prev => ({ ...prev, valid_to: e.target.value  }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
          <Button onClick={submit} data-testid="contract-create-submit">{t('auto.ctr.createDraft')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ===== Templates list ===============================================
function TemplatesList() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    const r = await axios.get(`${API}/contract-templates?active_only=false`);
    setTemplates(r.data || []);
  };
  useEffect(() => { reload(); }, []);

  const remove = async (tid) => {
    if (!window.confirm(t('auto.ctr.archiveThisTemplateExistingContractsUsing'))) return;
    try { await axios.delete(`${API}/contract-templates/${tid}`); toast.success('Archived'); reload(); }
    catch (e) { toast.error(t('auto.ctr.deleteFailed')); }
  };

  const copyAsNew = async (t) => {
    if (!window.confirm(`Duplicate "${t.name}" as a new editable template?`)) return;
    try {
      const r = await axios.post(`${API}/contract-templates/${t.id}/copy`);
      toast.success(`Copied as "${r.data.name}"`);
      await reload();
      // Open the new copy immediately so the user can rename / tweak it
      setEditing(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copy failed');
    }
  };

  const cloneInOtherLanguage = async (tItem) => {
    // Flip the target language based on the source's current one so a
    // single button always produces the *missing* sibling.
    const sourceLang = (tItem.language || 'en').toLowerCase();
    const targetLang = sourceLang === 'pl' ? 'en' : 'pl';
    const suffix = targetLang === 'pl' ? t('auto.ctr.pl') : t('auto.ctr.en');
    if (!window.confirm(
      `Clone "${tItem.name}" as a ${targetLang.toUpperCase()} sibling? A new template named "${tItem.name} ${suffix}" will be created, linked to the original, and opened for editing so you can translate the paragraphs.`
    )) return;
    try {
      const r = await axios.post(
        `${API}/contract-templates/${tItem.id}/clone-language`,
        { language: targetLang },
      );
      toast.success(`Created "${r.data.name}"`);
      await reload();
      setEditing(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clone failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> {t('auto.ctr.templates')}</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="template-new-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('auto.ctr.newTemplate')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">{t('auto.ctr.noTemplatesYetCreateOneTo')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.name')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.kind')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.lang')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.pattern')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.paragraphs')}</th>
                <th className="text-right px-2 py-1.5 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tItem) => (
                <tr key={tItem.id} className="border-t" data-testid={`template-row-${tItem.id}`}>
                  <td className="px-2 py-2">
                    {tItem.name}
                    {!tItem.is_active && <Badge variant="outline" className="ml-2 text-[10px]">archived</Badge>}
                  </td>
                  <td className="px-2 py-2"><Badge variant="outline">{KIND_LABEL[tItem.kind]}</Badge></td>
                  <td className="px-2 py-2 uppercase">{tItem.language}</td>
                  <td className="px-2 py-2 font-mono text-xs">{tItem.numbering_pattern}</td>
                  <td className="px-2 py-2">{(tItem.paragraphs || []).length}</td>
                  <td className="px-2 py-2 text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(tItem)} title={t('auto.ctr.edit')}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => copyAsNew(tItem)}
                      title={t('auto.ctr.copyAsNew')}
                      data-testid={`template-copy-${tItem.id}`}
                    >
                      <Copy className="h-3.5 w-3.5 text-blue-600" />
                    </Button>
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => cloneInOtherLanguage(tItem)}
                      title={(tItem.language || 'en').toLowerCase() === 'pl' ? t('auto.ctr.cloneInEnglish') : t('auto.ctr.cloneInPolish')}
                      data-testid={`template-clone-lang-${tItem.id}`}
                    >
                      <Languages className="h-3.5 w-3.5 text-emerald-600" />
                    </Button>
                    {tItem.is_active && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(tItem.id)} title={t('auto.ctr.archive')}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {(editing || creating) && (
        <TemplateEditor
          template={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </Card>
  );
}
